import React, { useEffect, useMemo, useState } from 'react';
import { Button, Header, Icon, Message, Segment } from 'semantic-ui-react';
import { submitOauthConsent } from '~/api/admin';
import { useUserContext } from '~/hooks';

/** Best-effort display name from the opaque client_id (a signed JWT); never trusted server-side. */
function clientNameFromId(clientId: string | null): string {
  if (!clientId) {
    return 'An MCP client';
  }
  try {
    const payload = clientId.split('.')[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof decoded?.client_name === 'string' && decoded.client_name ? decoded.client_name : 'An MCP client';
  } catch {
    return 'An MCP client';
  }
}

function redirectHost(redirectUri: string | null): string | null {
  if (!redirectUri) {
    return null;
  }
  try {
    return new URL(redirectUri).host;
  } catch {
    return null;
  }
}

export default function OAuthAuthorize() {
  const { loggedInUser } = useUserContext();
  const [pending, setPending] = useState<'approve' | 'deny' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const params = useMemo(() => {
    const search = new URLSearchParams(window.location.search);
    const out: { [name: string]: string } = {};
    search.forEach((value, name) => {
      out[name] = value;
    });
    return out;
  }, []);

  useEffect(() => {
    document.title = 'Authorize MCP access | Conductor Solar';
  }, []);

  const clientName = clientNameFromId(params.client_id ?? null);
  const host = redirectHost(params.redirect_uri ?? null);

  const decide = async (decision: 'approve' | 'deny') => {
    setPending(decision);
    setError(null);
    try {
      const { redirectTo } = await submitOauthConsent(decision, params);
      window.location.assign(redirectTo);
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data?.message ?? 'Could not complete the authorization. Please try again.');
      setPending(null);
    }
  };

  if (!params.client_id || !params.redirect_uri) {
    return (
      <div style={{ padding: '2rem', maxWidth: '40rem' }}>
        <Message negative>This authorization link is incomplete. Start the connection again from Claude.</Message>
      </div>
    );
  }

  if (!loggedInUser?.isAdmin) {
    return (
      <div style={{ padding: '2rem', maxWidth: '40rem' }}>
        <Message negative>
          <Message.Header>Admin account required</Message.Header>
          Only Conductor admin users can authorize MCP access. You are signed in as {loggedInUser?.email}.
        </Message>
        <Button onClick={() => decide('deny')} loading={pending === 'deny'} disabled={pending !== null}>
          Return to {clientName}
        </Button>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '40rem' }}>
      <Segment>
        <Header as="h2">
          <Icon name="plug" />
          <Header.Content>
            {clientName} wants read-only access to Conductor
            <Header.Subheader>Signed in as {loggedInUser.email}</Header.Subheader>
          </Header.Content>
        </Header>
        <p>If you approve, {clientName} will be able to, on your behalf:</p>
        <ul>
          <li>Look up users, companies, projects, portfolios, activity and notifications</li>
          <li>Run read-only SQL queries and browse the database schema</li>
        </ul>
        <p>
          It will <strong>not</strong> be able to change any data. Access is tied to your admin account and can be
          revoked at any time from Admin → Claude MCP Access or by removing the connector in Claude.
        </p>
        {host && (
          <p style={{ color: 'grey' }}>
            After you decide, you will be sent back to <code>{host}</code>.
          </p>
        )}
        {error && <Message negative>{error}</Message>}
        <Button primary onClick={() => decide('approve')} loading={pending === 'approve'} disabled={pending !== null}>
          Approve
        </Button>
        <Button onClick={() => decide('deny')} loading={pending === 'deny'} disabled={pending !== null}>
          Deny
        </Button>
      </Segment>
    </div>
  );
}
